import {makeAutoObservable, reaction} from 'mobx';

import type {PostExpandedCommon} from '../../../../api/channel/post';
import type {MaterialExpandedCommon} from '../../../../api/material';
import type {FileApiModel} from '../../../../api/media/file';
import type {CoreClientChannelsPostsListParams} from '../../../../api/types/core/methods';
import type {CollectionStore} from '../../../../api/utils/collectionStore';
import type {Destructible, WithReactionDispose} from '../../../../utils/store';
import type {Attachment, CollectionConfig as Config} from './types';

import {postExpandCommon} from '../../../../api/channel/post';
import {createCollectionStore} from '../../../../api/utils/collectionStore';
import {getUniqueArrayByKey} from '../../../../utils/array';
import {isMaterialAttachment} from '../../../../utils/entity/post/attachmentTypeCheck';
import {ReactionDisposeManager} from '../../../../utils/store';
import {
  buildQuery,
  getAttachmentTypeByType,
  getCategoriesByType,
} from './utils';

export class ChannelAttachmentsCollectionStore
  implements Destructible, WithReactionDispose
{
  static DEFAULT_LIMIT = 20;
  attachments: Attachment[] = [];
  channelId: Config['channelId'];
  disposer = new ReactionDisposeManager();
  limit: Config['limit'];
  listStore: CollectionStore<
    PostExpandedCommon,
    CoreClientChannelsPostsListParams
  > = createCollectionStore({
    method: 'client.channels.posts.list',
  });
  type: Config['type'];

  constructor(config: Config) {
    this.channelId = config.channelId;
    this.type = config.type;
    this.limit =
      config.limit || ChannelAttachmentsCollectionStore.DEFAULT_LIMIT;

    makeAutoObservable(
      this,
      {},
      {
        autoBind: true,
      },
    );

    this.disposer.watch(
      'items',
      reaction(
        () => this.listStore.items.length,
        () => {
          let newAttachments: Attachment[] = [];

          const isSuitableMaterialAttachment = (
            file: FileApiModel | null,
          ): file is FileApiModel => {
            return (
              !!file && this.isSuitableAttachment(file.categories, file.type)
            );
          };

          this.listStore.items.slice().forEach(post => {
            post.expandedAttachments.forEach(attachment => {
              if (attachment) {
                if (
                  // @ts-expect-error: TODO: describe why
                  // TODO: fix isMaterialAttachment TS signature
                  isMaterialAttachment(attachment as Material)
                ) {
                  newAttachments.push(
                    ...(attachment as MaterialExpandedCommon).files
                      .filter(isSuitableMaterialAttachment)
                      .map(file => ({
                        ...file,
                        user_name: post.user?.name,
                      })),
                  );
                } else if (
                  this.isSuitableAttachment(
                    (attachment as FileApiModel).categories,
                    (attachment as FileApiModel).type,
                  )
                ) {
                  newAttachments.push({
                    ...(attachment as FileApiModel),
                    user_name: post.user?.name,
                  });
                }
              }
            });
          });

          newAttachments = getUniqueArrayByKey(
            this.attachments.concat(newAttachments),
            '_id',
          );

          this.attachments = newAttachments;
        },
      ),
    );
  }

  destruct() {
    this.disposer.dispose();

    this.listStore.clear();
  }

  async fetch(silent = false) {
    await this.listStore.fetch(this.storeParams, {
      silent,
    });
  }

  async fetchMore() {
    if (this.listStore.cursor.next) {
      await this.listStore.fetch(
        {
          ...this.storeParams,
          cursor: this.listStore.cursor.next!,
        },
        {
          silent: true,
        },
      );
    }
  }

  private get storeParams() {
    const params: CoreClientChannelsPostsListParams = {
      channel_id: this.channelId,
      cursor: [-1, 0, null],
      expand: postExpandCommon,
      limit: this.limit,
      query: buildQuery(this.type),
    };

    return params;
  }

  private isSuitableAttachment(
    categories: FileApiModel['categories'],
    type: FileApiModel['type'],
  ) {
    return !!(
      type === getAttachmentTypeByType(this.type) &&
      categories.filter(category =>
        getCategoriesByType(this.type).includes(category),
      ).length
    );
  }
}
